/**
 * Created by rathompson on 10/12/23
 * File: TenantSearch.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import { useEffect, useState, useCallback, VFC } from 'react'
import { Input, Body, Button } from '@panwds/react-ui'
import { RemoveIcon, SearchIcon } from '@panwds/icons'
import { LABEL_NO_RESULTS, LABEL_SEARCH_RESULTS } from '../../../tenantSwitcher/constants/LabelConstants'
import { ARRAY } from '../../../common/utils/Utils'
import type { Tsg } from '@sparky/framework'

const TenantSearch: VFC<{
  onSelectTenant?: (id: string) => void,
  treeData: Tsg[]
}> = ({
  onSelectTenant,
  treeData,
}) => {

  const [display, setDisplay] = useState(false)
  const [suggestions, setSuggestions] = useState<Array<{ tenant: Tsg, parent?: Tsg }>>([])
  const [searchInput, setSearchInput] = useState('')

  const searchTenant = useCallback((tenant, array, parent) => {
    if (tenant?.display_name?.toLowerCase().includes(searchInput?.toLowerCase()) || String(tenant?.id).startsWith(searchInput)) {
      if (tenant?.id !== '_superRoot') {
        array?.push({
          tenant,
          parent,
        })
      }
    }
    if (ARRAY.hasArray(tenant?.children)) {
      for (const child of tenant.children) {
        searchTenant(child, array, tenant)
      }
    }
    return array.filter(({ tenant }: any) => !tenant.disabled)
  }, [searchInput])

  //Debouncing logic used to identify 300ms key press.
  useEffect(() => {
    setSuggestions(() => {
      return !searchInput?.trim().length ? [] : searchTenant(treeData[0], [], undefined)
    })
  }, [searchInput, treeData, searchTenant])

  useEffect(() => {
    if (suggestions.length) {
      setDisplay(true)
    }
    else if (searchInput && !suggestions.length) {
      setDisplay(true)
    }
    else {
      setDisplay(false)
    }
  }, [searchInput, suggestions])

  // Search Validation Logic used
  const updateSelection = (value: any, id: any) => {
    setSearchInput('')
    setDisplay(false)
    if (onSelectTenant) {
      onSelectTenant(id)
    }
  }
  const searchInPutHandler = (event: any) => {
    setSearchInput(event?.target?.value)
  }

  const bolderize = (text: string) => {
    if (!text) {
      return null
    }
    const foundIdx = text.toLowerCase().indexOf(searchInput.trim().toLowerCase())
    if (foundIdx < 0) {
      return <span>{text}</span>
    }
    const toIdx = foundIdx + searchInput.length
    return (
      <span>
        {text.slice(0, foundIdx)}
        <mark className='tw-bg-blue-200 hover:tw-text-blue-700 tw-text-blue-600 dark:tw-bg-gray-600 dark:tw-text-blue-200'>{text.slice(foundIdx, toIdx)}</mark>
        {text.slice(toIdx)}
      </span>
    )
  }

  return (
    <>
      <div className='cm-p-2 cm-border cm-border-solid cm-border-gray-300 cm-border-t-0 cm-border-l-0 cm-border-r-0'>
        <Input
          addClassName="cm-w-full"
          prefix={<SearchIcon size='sm' />}
          suffix={searchInput ? (
            <Button
              aria-label="clear"
              appearance="tertiary-clear"
              onClick={() => setSearchInput('')}
              size='sm'
              icon={<RemoveIcon size='sm' />}
            />
          ) : null}
          placeholder="Search Tenant"
          value={searchInput}
          onChange={searchInPutHandler}
        />
      </div>
      {display && (
        <div className="cm-overflow-y-scroll cm-overflow-x-hidden" style={{ height: 'calc(24rem - 42px)' }}>
          <Body addClassName="cm-p-2" size="lg">{LABEL_SEARCH_RESULTS}</Body>
          {suggestions.length ? suggestions.map(({ tenant, parent }, index) => {
            return (
              <div
                key={index}
                className='hover:cm-bg-blue-200 cm-cursor-pointer'
                onClick={() => (tenant.disabled ? null : updateSelection(tenant.display_name, tenant.id))}
              >
                <div className='cm-p-2'>
                  {parent && <Body size="lg" data-tsg-id={parent.id}>{bolderize(parent.display_name)}</Body>}
                  <div tabIndex={0} data-tsg-id={tenant?.id}>
                    <Body addClassName='cm-truncate cm-m-0 cm-text-blue' size="lg">
                      {bolderize(tenant.display_name)}
                    </Body>
                  </div>
                </div>
              </div>
            )
          }) : (
            <div className="cm-p-2">
              {LABEL_NO_RESULTS}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default TenantSearch

